import apolloClient from "../../../config/apolloClient";
import { IUser } from "../../interfaces/user.interface";
import { STORAGE_KEYS } from "../../routes/Constants";
import { readJson } from "../../utils/storage";
import { UpdateUser } from "../user/user.gql";
import { toast } from "react-toastify";
import {
    createBridgeTokenMutation,
    exchangeRefreshTokenMutation,
    getHubspotTokenMutation,
    loggedInUserQuery,
    loggedUserWithStudentQuery,
    loginFromAdminMutation,
    loginMutation,
    logoutMutation,
    registerMutation,
    requestChangePasswordMutation,
    requestNewPasswordMutation,
} from "./auth.gql";

interface ILoginPayload {
    email: string;
    password: string;
}

export interface IRegisterPayload {
    email: string;
    password?: string;
    firstname: string;
    lastname: string;
    isB2cParent?: boolean;
    freeUser?: boolean;
    captchaToken?: string;
}

/**
 * Best-effort revocation of the server-side session behind the stored refresh token.
 * Fire-and-forget: logging out must always proceed locally, so any failure (network,
 * already-expired token) is swallowed. Call this BEFORE clearLocalStorage, while the
 * token is still readable.
 */
export const revokeStoredSession = () => {
    try {
        const raw = localStorage.getItem("token");
        if (!raw) return;

        const { refreshToken } = JSON.parse(raw);
        if (!refreshToken) return;

        apolloClient
            .mutate({
                mutation: logoutMutation,
                variables: { input: { refreshToken } },
            })
            .catch(() => undefined);
    } catch {
        // ignore — a missing/corrupt token must not block logout
    }
};

/**
 * The stored session, or null. Never throws: JSON.parse("") and a corrupt value both would.
 */
export const readStoredSession = () =>
    readJson<{ accessToken?: string; refreshToken?: string } | null>(
        "token",
        null
    );

/**
 * Writes the session where the Apollo client reads it. Login flows call this before their first
 * authenticated request: the reducer also stores it, but under React 18 the reducer runs on the
 * next render, after that request has already gone out without a token.
 */
export const storeSessionToken = (token: {
    accessToken: string;
    refreshToken: string;
}) => {
    localStorage.setItem("token", JSON.stringify(token));
};

export const clearLocalStorage = () => {
    const instructionStateBakeACake = localStorage.getItem(
        "instructionStateBakeACake"
    );
    const instructionStateBuildACar = localStorage.getItem(
        "instructionStateBuildACar"
    );
    const gameSettings = localStorage.getItem(STORAGE_KEYS.GAME_SETTINGS);

    localStorage.clear();
    localStorage.setItem(
        "instructionStateBakeACake",
        instructionStateBakeACake || ""
    );
    localStorage.setItem(
        "instructionStateBuildACar",
        instructionStateBuildACar || ""
    );
    localStorage.setItem(STORAGE_KEYS.GAME_SETTINGS, gameSettings || "");
};

export const loggedInUserRequest = async () => {
    const { data, errors } = await apolloClient.query({
        query: loggedInUserQuery,
    });

    if (errors) {
        return { errors };
    }

    return { response: data.LoggedInUser };
};

export const loggedUserWithStudent = async () => {
    const { data, errors } = await apolloClient.query({
        query: loggedUserWithStudentQuery,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
    });

    if (errors) {
        return { errors };
    }

    return { response: data.findUserWithStudentsRelated };
};

export const setParentProfile = async (profileStudentUsed: string | null) => {
    const { data, errors } = await apolloClient.mutate({
        mutation: UpdateUser,
        variables: {
            updateUserInput: {
                profileStudentUsed,
            },
        },
    });

    if (errors) {
        toast.error(errors[0]?.message || "Something went wrong");
        throw new Error(errors[0]?.message || "Something went wrong");
    }

    return data.updateUser;
};

export const createBridgeToken = async (jwt: string) => {
    const { data, errors } = await apolloClient.mutate({
        mutation: createBridgeTokenMutation,
        variables: {
            input: {
                jwt,
                origin: "WEB",
            },
        },
    });

    if (errors) {
        return { errors };
    }

    return { response: data.CreateBridgeToken };
};

export const loginRequest = async (payload: ILoginPayload) => {
    const { data, errors } = await apolloClient.mutate({
        mutation: loginMutation,
        variables: {
            input: {
                ...payload,
                origin: "WEB",
            },
        },
    });

    if (errors) {
        return { errors };
    }

    return { response: data.Login };
};

export const loginFromAdminRequest = async (token: string) => {
    const { data, errors } = await apolloClient.mutate<{
        LoginFromWeb: {
            accessToken: string;
            refreshToken: string;
            user: IUser;
        };
    }>({
        mutation: loginFromAdminMutation,
        variables: {
            input: {
                token,
            },
        },
    });

    if (errors) {
        throw errors;
    }

    return { response: data?.LoginFromWeb };
};

export const exchangeRefreshTokenRequest = async (refreshToken: string) => {
    const { data, errors } = await apolloClient.mutate({
        mutation: exchangeRefreshTokenMutation,
        variables: {
            input: {
                refreshToken,
            },
        },
    });

    if (errors) {
        return { errors };
    }

    return { response: data.ExchangeRefreshToken };
};

export const registerRequest = async (payload: IRegisterPayload) => {
    const { data, errors } = await apolloClient.mutate({
        mutation: registerMutation,
        variables: {
            input: {
                ...payload,
            },
        },
    });

    if (errors) {
        return { errors };
    }

    return data.Registration;
};

export const newPasswordRequest = async (email: string) => {
    const { data, errors } = await apolloClient.mutate({
        mutation: requestNewPasswordMutation,
        variables: {
            input: {
                email,
            },
        },
    });

    if (errors) {
        return { errors };
    }

    return { response: data.RequestNewPassword };
};

export const changePasswordRequest = async (payload: {
    token: string;
    newPassword: string;
}) => {
    const { data, errors } = await apolloClient.mutate({
        mutation: requestChangePasswordMutation,
        variables: {
            input: {
                ...payload,
            },
        },
    });

    if (errors) {
        return { errors };
    }

    return { response: data.ChangePassword };
};

export const getHubspotToken = async (payload: {
    email: string;
    firstName: string;
    lastName: string;
}) => {
    const { data, errors } = await apolloClient.mutate({
        mutation: getHubspotTokenMutation,
        variables: {
            input: {
                ...payload,
            },
        },
    });

    if (errors) {
        return { errors };
    }

    return { response: data.getHubspotToken };
};
